import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, can, downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useAutosave } from "@/lib/useAutosave";
import { useAuth } from "@/context/AuthContext";
import { EstadoBadge, OrigenBadge, Spinner, AutosaveNote } from "@/components/common";
import { ArrowLeft, Download, Pencil, Save, X, History, FileText, Trash2 } from "lucide-react";
import { CertificadosEmpleado } from "@/components/CertificadosEmpleado";
import { HojaVidaEmpleado } from "@/components/HojaVidaEmpleado";
import { PeriodoPruebaCard } from "@/components/PeriodoPruebaCard";
import { EmpresaUsuariaSelect } from "@/components/EmpresaUsuariaSelect";
import { HistorialLaboral } from "@/components/HistorialLaboral";
import { GestionarAccesoBtn } from "@/components/GestionarAcceso";
import { HistorialFaltasRit } from "@/components/HistorialFaltasRit";
import { LlamadosAtencion } from "@/components/LlamadosAtencion";

function BotonTerminacion({ vid }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [on, setOn] = React.useState(false);
  if (!["admin", "gestor"].includes(user?.role)) return null;
  const solicitar = () => {
    if (on) return;
    setOn(true);
    api.post("/terminaciones", { vinculacion_id: vid }).then((r) => navigate(`/contrataciones/terminaciones/${r.data.terminacion.id}`))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo crear la solicitud"); setOn(false); });
  };
  return (
    <div className="flex justify-end">
      <button onClick={solicitar} disabled={on} data-testid="btn-solicitar-terminacion-ficha" className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50">Solicitar terminación de contrato</button>
    </div>
  );
}

const TABS = [
  { key: "personales", label: "Datos personales", group: "personales" },
  { key: "contacto", label: "Contacto", group: "contacto" },
  { key: "vinculacion", label: "Vinculación", group: "vinculacion" },
  { key: "hoja_vida", label: "Hoja de vida", perm: "empleados" },
  { key: "seguridad_social", label: "Seguridad social", group: "seguridad_social" },
  { key: "bancaria", label: "Información bancaria", group: "bancaria" },
  { key: "control", label: "Datos de control de la matriz" },
  { key: "documentos", label: "Documentos", perm: "empleados" },
  { key: "certificados", label: "Certificados laborales" },
  { key: "historial", label: "Historial de cambios" },
];

export default function EmpleadoDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("personales");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const { auto: autoDet, markSaved: markDetSaved } = useAutosave({
    data: form,
    enabled: editing,
    save: async (d) => {
      const { data: updated } = await api.patch(`/vinculaciones/${id}?autosave=true`, { campos: d });
      setDetail(updated); // refresca la vista sin salir del modo edición
    },
  });

  const load = () => {
    api.get(`/vinculaciones/${id}`).then((r) => setDetail(r.data)).catch(() => setDetail(false));
  };
  useEffect(load, [id]);

  const startEdit = () => {
    const f = {};
    Object.values(detail.grupos).forEach((fields) => fields.forEach((fld) => (f[fld.key] = fld.value)));
    setForm(f);
    markDetSaved(f);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/vinculaciones/${id}`, { campos: form });
      setDetail(data);
      setEditing(false);
      toast.success("Cambios guardados");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const exportOne = async () => {
    try { await downloadFile(`/export/vinculacion/${id}`, "matriz_empleado.xlsx"); toast.success("Empleado exportado"); }
    catch { toast.error("No fue posible exportar"); }
  };

  const removeVinc = async () => {
    if (!window.confirm("¿Eliminar esta vinculación? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/vinculaciones/${id}`);
      toast.success("Vinculación eliminada");
      navigate("/empleados");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (detail === null) return <div className="flex h-64 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>;
  if (detail === false) return <p className="text-sm text-slate-500">No fue posible cargar el expediente.</p>;

  const nombre = detail.grupos.personales.find((f) => f.key === "nombre_completo")?.value;
  const cedula = detail.grupos.personales.find((f) => f.key === "cedula")?.value;
  const currentGroup = TABS.find((t) => t.key === tab)?.group;

  const isDate = (k) => ["fecha_ingreso", "fecha_retiro", "fecha_nacimiento"].includes(k);

  return (
    <div className="space-y-5">
      <button onClick={() => navigate("/empleados")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-vino-700" data-testid="btn-volver">
        <ArrowLeft className="h-4 w-4" /> Volver a Empleados
      </button>

      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-vino-700 text-xl font-semibold text-white">
            {(nombre || "?").slice(0, 1).toUpperCase()}
          </div>
          <div>
            <h1 className="font-display text-xl font-bold text-slate-900 sm:text-2xl" data-testid="detalle-nombre">{nombre || "(sin nombre)"}</h1>
            <p className="text-sm text-slate-400">Documento: {cedula || "—"}</p>
            <div className="mt-2 flex gap-2">
              <OrigenBadge origen={detail.origen} testid="detalle-origen" />
              <EstadoBadge estado={detail.estado} testid="detalle-estado" />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {can(user, "exportar") && (
            <button onClick={exportOne} data-testid="detalle-exportar" className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-vino-700 hover:bg-vino-50">
              <Download className="h-4 w-4" /> Exportar
            </button>
          )}
          {can(user, "editar") && !editing && currentGroup && (
            <button onClick={startEdit} data-testid="detalle-editar" className="flex items-center gap-2 rounded-lg bg-vino-700 px-3.5 py-2 text-sm font-semibold text-white hover:bg-vino-800">
              <Pencil className="h-4 w-4" /> Editar
            </button>
          )}
          {can(user, "eliminar") && !editing && (
            <button onClick={removeVinc} data-testid="detalle-eliminar" className="flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3.5 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
              <Trash2 className="h-4 w-4" /> Eliminar
            </button>
          )}
          {editing && (
            <>
              <AutosaveNote auto={autoDet} testid="detalle-autosave-status" />
              <button onClick={() => setEditing(false)} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3.5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
                <X className="h-4 w-4" /> Cancelar
              </button>
              <button onClick={save} disabled={saving} data-testid="detalle-guardar" className="flex items-center gap-2 rounded-lg bg-vino-700 px-3.5 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60">
                {saving ? <Spinner /> : <Save className="h-4 w-4" />} Guardar
              </button>
            </>
          )}
        </div>
      </div>

      <PeriodoPruebaCard vid={id} />
      <HistorialLaboral vid={id} />
      <HistorialFaltasRit vid={id} />
      <LlamadosAtencion vid={id} />
      <div className="flex flex-wrap justify-end gap-2">
        {can(user, "usuarios") && <GestionarAccesoBtn vid={id} />}
        <BotonTerminacion vid={id} />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200 pb-px">
        {TABS.filter((t) => !t.perm || can(user, t.perm)).map((t) => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => { setTab(t.key); setEditing(false); }}
            className={`whitespace-nowrap border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors ${
              tab === t.key ? "border-vino-700 text-vino-800" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        {currentGroup && (
          <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {detail.grupos[currentGroup].map((fld) => (
              <div key={fld.key}>
                <label className="block text-xs font-medium uppercase tracking-wide text-slate-400">
                  {fld.label} <span className="text-slate-300">· {fld.pos}</span>
                </label>
                {editing && fld.key === "empresa_cliente" ? (
                  <div className="mt-1"><EmpresaUsuariaSelect value={form[fld.key] ?? ""} onChange={(v) => setForm((f) => ({ ...f, [fld.key]: v }))} testid="edit-empresa_cliente" /></div>
                ) : editing ? (
                  <input
                    data-testid={`edit-${fld.key}`}
                    type={isDate(fld.key) ? "date" : "text"}
                    value={form[fld.key] ?? ""}
                    onChange={(e) => setForm((f) => ({ ...f, [fld.key]: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20"
                  />
                ) : (
                  <p className="mt-1 text-sm font-medium text-slate-800">{fld.value || <span className="text-slate-300">—</span>}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "control" && (
          <div>
            <p className="mb-4 text-xs text-slate-500">
              Las 52 posiciones originales de la matriz, conservadas por posición (incluidas columnas ambiguas y vacía).
            </p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-sm" data-testid="tabla-matriz-control">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2 font-semibold">Col</th>
                    <th className="px-3 py-2 font-semibold">Encabezado original</th>
                    <th className="px-3 py-2 font-semibold">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.matriz_control.map((c) => (
                    <tr key={c.index} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">{c.columna}</td>
                      <td className="px-3 py-1.5 text-slate-600">{c.encabezado}</td>
                      <td className="px-3 py-1.5 font-medium text-slate-800">{c.value || <span className="text-slate-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "hoja_vida" && <HojaVidaEmpleado vid={id} />}
        {tab === "documentos" && <HojaVidaEmpleado vid={id} soloDocumentos />}

        {tab === "certificados" && <CertificadosEmpleado vid={id} />}

        {tab === "historial" && (
          <div className="space-y-3" data-testid="historial-cambios">
            {(!detail.historial || detail.historial.length === 0) ? (
              <p className="py-8 text-center text-sm text-slate-400">Sin cambios registrados.</p>
            ) : (
              detail.historial.slice().reverse().map((h, i) => (
                <div key={i} className="flex gap-3 border-b border-slate-100 pb-3 last:border-0">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-vino-50 text-vino-700">
                    <History className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{h.accion}</p>
                    <p className="text-sm text-slate-500">{h.detalle}</p>
                    <p className="mt-0.5 text-xs text-slate-400">
                      {new Date(h.fecha).toLocaleString("es-CO")} · {h.usuario}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
